import { RiLoader2Fill } from '@remixicon/react';
import { Counter } from './counter';
import { renderIcon, type IconLike } from './icon';
import { tv, type VariantProps } from './lib/tv';
import { Tooltip } from './tooltip';

/**
 * IconButton — kit 67:10331 (spec §4.2) + product sizes:
 * `xs` 24 slot without fill (panel row actions, icon 14) · `sm` 24 · `row` 28 r8 (table/shortcut rows) ·
 * `md` 32 · `tool` 36 r8 (floating toolbars, direction pad) · `lg` 40.
 */
export const iconButtonStyles = tv({
  slots: {
    base: [
      'group relative inline-flex shrink-0 cursor-pointer items-center justify-center',
      'transition-[background-color,color,opacity] duration-150 ease-standard',
      'disabled:cursor-not-allowed disabled:opacity-40',
    ],
    icon: 'shrink-0',
  },
  variants: {
    size: {
      xs: { base: 'size-24', icon: 'size-14' },
      sm: { base: 'size-24', icon: 'size-16' },
      row: { base: 'size-28', icon: 'size-16' },
      md: { base: 'size-32', icon: 'size-16' },
      tool: { base: 'size-36', icon: 'size-18' },
      lg: { base: 'size-40', icon: 'size-20' },
    },
    variant: {
      ghost: { base: 'bg-transparent text-icon hover:bg-hover hover:text-fg' },
      secondary: { base: 'bg-subtle text-icon hover:bg-field hover:text-fg' },
      /** White/10 — stepper −/+, table row actions, direction pad. */
      field: { base: 'bg-field text-icon hover:bg-line-strong hover:text-fg' },
      /** White/3 — floating map/3D toolbar buttons. */
      faint: { base: 'bg-white-30 text-icon hover:bg-hover hover:text-fg light:bg-black-30' },
      primary: { base: 'bg-accent text-white hover:bg-[color-mix(in_srgb,var(--color-accent)_88%,white)]' },
      'primary-alt': { base: 'bg-accent-2 text-white hover:bg-[color-mix(in_srgb,var(--color-accent-2)_88%,white)]' },
      soft: { base: 'bg-accent-soft text-accent hover:bg-[color-mix(in_srgb,var(--color-accent)_30%,transparent)]' },
      destructive: { base: 'bg-tone-red text-white hover:bg-tone-red-lighter' },
      'destructive-soft': { base: 'bg-tone-red-10 text-tone-red hover:bg-tone-red-20' },
      /** Bare red glyph — panel delete actions (`#e1020299`). */
      danger: { base: 'bg-transparent text-icon-danger hover:bg-dark-red-100 hover:text-danger' },
    },
    shape: { rounded: '', circle: { base: 'rounded-full' } },
    active: { true: '' },
    activeTone: { accent: '', accent2: '' },
  },
  compoundVariants: [
    { shape: 'rounded', size: ['xs', 'sm'], class: { base: 'rounded-4' } },
    { shape: 'rounded', size: ['row', 'md', 'tool', 'lg'], class: { base: 'rounded-8' } },
    { active: true, activeTone: 'accent', class: { base: 'bg-accent text-white hover:bg-accent hover:text-white' } },
    { active: true, activeTone: 'accent2', class: { base: 'bg-accent-2 text-white hover:bg-accent-2 hover:text-white' } },
  ],
  defaultVariants: { size: 'md', variant: 'ghost', shape: 'rounded', activeTone: 'accent' },
});

export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    VariantProps<typeof iconButtonStyles> {
  ref?: React.Ref<HTMLButtonElement>;
  icon: IconLike;
  /** Required: icon-only buttons need an accessible name (Uzbek, e.g. "Yopish"). */
  'aria-label': string;
  /** Notification counter overlay (header bell: `#f48200` with a 2px panel ring). */
  badge?: number;
  /** Show the `aria-label` (or this node) in a tooltip on hover/focus. */
  tooltip?: React.ReactNode | true;
  loading?: boolean;
  /** Selected tool / toggle state (also sets `aria-pressed`). */
  active?: boolean;
}

export function IconButton({
  icon,
  size,
  variant,
  shape,
  active,
  activeTone,
  badge,
  tooltip,
  loading,
  disabled,
  type = 'button',
  className,
  onClick,
  ref,
  ...rest
}: IconButtonProps) {
  const s = iconButtonStyles({ size, variant, shape, active, activeTone });
  const button = (
    <button
      {...rest}
      ref={ref}
      type={type}
      disabled={disabled}
      aria-disabled={loading || rest['aria-disabled'] || undefined}
      aria-busy={loading || undefined}
      onClick={loading ? (e) => e.preventDefault() : onClick}
      aria-pressed={active ?? rest['aria-pressed']}
      // the counter is decorative (aria-hidden) — announce it with the name instead
      aria-label={badge ? `${rest['aria-label']} (${badge})` : rest['aria-label']}
      className={s.base({ class: className })}
    >
      {loading ? <RiLoader2Fill aria-hidden className={`${s.icon()} animate-spin`} /> : renderIcon(icon, s.icon())}
      {badge != null && badge > 0 && (
        <Counter
          value={badge}
          max={99}
          tone="notify"
          variant="filled"
          ring
          aria-hidden
          className="pointer-events-none absolute top-0 left-16"
        />
      )}
    </button>
  );
  if (!tooltip) return button;
  return <Tooltip content={tooltip === true ? rest['aria-label'] : tooltip}>{button}</Tooltip>;
}
